import { v4 as uuidv4 } from 'uuid';

/**
 * Generate a unique entity ID
 */
export function generateId(): string {
    return uuidv4();
}

/**
 * Clamp a number between min and max
 */
export function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

/**
 * Linear interpolation
 */
export function lerp(a: number, b: number, t: number): number {
    return a + (b - a) * t;
}

/**
 * Convert degrees to radians
 */
export function degToRad(degrees: number): number {
    return degrees * (Math.PI / 180);
}

/**
 * Convert radians to degrees
 */
export function radToDeg(radians: number): number {
    return radians * (180 / Math.PI);
}

/**
 * Calculate distance between two 3D points
 */
export function distance3D(
    p1: [number, number, number],
    p2: [number, number, number]
): number {
    const dx = p2[0] - p1[0];
    const dy = p2[1] - p1[1];
    const dz = p2[2] - p1[2];
    return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/**
 * Calculate distance between two 2D points
 */
export function distance2D(
    p1: [number, number],
    p2: [number, number]
): number {
    const dx = p2[0] - p1[0];
    const dy = p2[1] - p1[1];
    return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Snap value to nearest grid increment
 */
export function snapToGrid(value: number, gridSize: number = 1): number {
    return Math.round(value / gridSize) * gridSize;
}

/**
 * Snap 3D point to grid
 */
export function snapPoint3D(
    point: [number, number, number],
    gridSize: number = 1
): [number, number, number] {
    return [
        snapToGrid(point[0], gridSize),
        snapToGrid(point[1], gridSize),
        snapToGrid(point[2], gridSize),
    ];
}

/**
 * Format number for display (e.g., in properties panel)
 */
export function formatNumber(value: number, decimals: number = 2): string {
    return value.toFixed(decimals);
}

/**
 * Parse user input to number with fallback
 */
export function parseNumber(input: string, fallback: number = 0): number {
    const parsed = parseFloat(input);
    return isNaN(parsed) ? fallback : parsed;
}

/**
 * Debounce function calls
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
    fn: T,
    delay: number
): (...args: Parameters<T>) => void {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: Parameters<T>) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
}

/**
 * Throttle function calls
 */
export function throttle<T extends (...args: unknown[]) => unknown>(
    fn: T,
    limit: number
): (...args: Parameters<T>) => void {
    let inThrottle = false;
    return (...args: Parameters<T>) => {
        if (!inThrottle) {
            fn(...args);
            inThrottle = true;
            setTimeout(() => (inThrottle = false), limit);
        }
    };
}
